<template>
  <div class="upload-page">
    <!-- 圆形退出按钮 -->
    <div class="logout-button-container">
      <button class="logout-button" @click="handleLogout" title="退出登录">
        <i class="el-icon-switch-button"></i>
      </button>
    </div>
    
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>文件上传</h2>
      <p>上传文件并设置分类和共享选项</p>
    </div>

    <!-- 主内容区 -->
    <div class="main-container">

    <el-button 
      icon="el-icon-back" 
      type="text" 
      @click="goBack"
      class="back-btn"
    >
      返回上一页
    </el-button>
      <!-- 文件属性设置 -->
      <el-form :model="formData" label-width="100px" class="file-form">
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="文件分类" required>
              <el-select 
                v-model="selectedCategoryId" 
                placeholder="选择分类"
                class="full-width"
                @change="checkButtonStatus"
              >
                <el-option v-for="item in categories"
                  :key="item.id"
                  :value="item.id"
                  :label="item.categoryName"
                >
                </el-option>
              </el-select>
            </el-form-item>
          </el-col>
          
          <el-col :xs="24" :sm="12" :md="8">
            <el-form-item label="是否共享">
              <el-switch 
                v-model="formData.isShared" 
                :active-value="1" 
                :inactive-value="0"
                active-color="#409eff"
              ></el-switch>
            </el-form-item>
          </el-col>
          
          <el-col :xs="24" :sm="12" :md="8" v-if="formData.isShared">
            <el-form-item label="共享时长">
              <el-select 
                v-model="formData.shareDuration" 
                placeholder="选择时长"
                class="full-width"
              >
                <el-option label="1天" value="1"></el-option>
                <el-option label="7天" value="7"></el-option>
                <el-option label="30天" value="30"></el-option>
                <el-option label="永久" value="999"></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <!-- 拖拽上传区域（ -->
      <div class="upload-wrapper">
        <div 
          class="upload-container"
          :class="{ 'is-dragging': isDragging }"
          @dragover.prevent="handleDragOver"
          @dragleave="handleDragLeave"
          @drop.prevent="handleDrop"
        >
          <i class="el-icon-upload upload-icon"></i>
          <div class="upload-text">
            <div class="title">拖拽文件至此处上传</div>
            <div class="subtitle">支持文档、图片、压缩包等文件，单个文件最大 500MB</div>
            <el-button class="upload-btn" size="small" type="primary" @click="handleClick" :disabled="isUploading">
              选择文件
            </el-button>
          </div>
          <input 
            ref="fileInput" 
            type="file" 
            class="file-input" 
            multiple
            @change="handleFileChange"
          >
        </div>

        <!-- 操作按钮 -->
        <div class="action-buttons">
          <el-button 
            type="primary" 
            @click="startUpload"
            :disabled="isUploadDisabled"
            class="start-btn"
            :loading="isUploading"
          >
            <i class="el-icon-upload"></i> {{ isUploading ? '上传中...' : '开始上传' }}
          </el-button>
          <el-button 
            type="default" 
            @click="clearAllFiles"
            :disabled="fileList.length === 0 || isUploading"
            class="reset-btn"
          >
            <i class="el-icon-refresh"></i> 重新选择
          </el-button>
        </div>
      </div>

      <!-- 支持的格式 -->
      <div class="supported-formats">
        <span>支持格式：</span>
        <el-tag 
          v-for="fmt in formats" 
          :key="fmt" 
          size="mini"
        >{{ fmt }}</el-tag>
      </div>

      <!-- 文件列表 -->
      <div class="file-list" v-if="fileList.length > 0">
        <div class="queue-heading"><span>上传队列 · {{ fileList.length }} 个文件</span><small>上传期间请保持页面打开</small></div>
        <div class="file-item" v-for="(file, index) in fileList" :key="index">
          <div class="file-info">
            <i class="el-icon-document file-icon"></i>
            <div class="file-meta">
              <div class="file-name">{{ file.name }}</div>
              <div class="file-size">{{ formatSize(file.size) }}</div>
            </div>
          </div>
          <div class="file-progress" v-if="file.progress !== undefined">
            <el-progress 
              :percentage="file.progress" 
              :stroke-width="2" 
              :text-inside="true"
              v-if="file.progress < 100"
            ></el-progress>
            <span class="status-text" v-else>正在保存文件…</span>
          </div>
          <el-button 
            icon="el-icon-delete" 
            size="mini" 
            class="delete-btn"
            @click="handleDelete(index)"
            :disabled="isUploading"
            aria-label="从上传队列移除文件"
          ></el-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getToken } from '@/utils/auth'
import { logout as logoutApi } from '@/api/auth';
import { removeToken } from '@/utils/auth';
import { uploadFile,getCategories } from '@/api/file'

export default {
  data() {
    return {
      formData: {
        isShared: 0,
        shareDuration: '7'
      },
      categories: [],
      fileList: [],
      formats: ['jpg', 'png', 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'zip', 'rar'],
      uploadHeaders: {
        Authorization: `Bearer ${getToken()}`
      },
      isDragging: false,
      selectedCategoryId: '',
      isUploading: false, // 上传状态控制
    }
  },

 created() {
    this.fetchCategories()
  },

  computed: {
    // 修复：简化禁用逻辑，确保按钮状态正确
    isUploadDisabled() {
      return this.isUploading || this.selectedCategoryId === '' || this.fileList.length === 0
    }
  },
  methods: {
    // 退出登录
    handleLogout() {
      this.$confirm('确定要退出登录吗?', '确认退出', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        // 调用后端退出登录API
        logoutApi().then(() => {
          // 清除本地token
          removeToken();
          // 跳转到登录页
          this.$router.push('/login');
          this.$message.success('退出登录成功');
        }).catch(() => {
          // 即使API调用失败，也清除token并跳转
          removeToken();
          this.$router.push('/login');
          this.$message.success('退出登录成功');
        });
      }).catch(() => {
        // 用户取消退出
      });
    },
    
    goBack() {
      window.history.back();
      // 若使用Vue Router可替换为：
      // this.$router.go(-1);
    },
    
    handleDragOver() { 
      this.isDragging = true; 
    },

    handleDragLeave() {
      this.isDragging = false;
    },

    handleDrop(event) {
      this.isDragging = false;
      if (!this.isUploading && event.dataTransfer) this.addFilesToUpload(event.dataTransfer.files);
    },

    handleClick() {
       if (this.isUploading) return;
       this.$refs.fileInput.click();
    },
    

    handleFileChange(e) {
      const files = e.target.files;
      if (files.length) {
        this.addFilesToUpload(files);
        // 清空input值，允许重复选择同一文件
        e.target.value = '';
      }
    },

    fetchCategories() {
      getCategories().then(res => {
        this.categories = res.data;
      }).catch(err => {
        this.$message.error('获取分类失败: ' + (err.message || err));
      });
    },

    // 新增：统一处理文件添加
    addFilesToUpload(files) {
      if (this.isUploading) return;
      // 最大文件大小限制：500MB
      const MAX_SIZE = 500 * 1024 * 1024;

      // 将File对象转换为文件列表项
      const newFiles = Array.from(files).map(file => {
        // 检查文件大小
        if (file.size > MAX_SIZE) {
          this.$message.warning(`文件 ${file.name} 超过500MB限制，已忽略`);
          return null;
        }
        return {
          name: file.name,        // 文件名
          size: file.size,        // 文件大小
          progress: 0,            // 上传进度（初始0）
          status: 'ready',        // 状态标识
          raw: file               // 原始File对象（用于实际上传）
        };
      }).filter(item => item !== null);

      // 添加到文件列表
      this.fileList = [...this.fileList, ...newFiles];
      console.log('文件列表更新:', this.fileList); // 调试用
    },
    
    // 开始上传
    startUpload() {
      if (this.isUploadDisabled) return
      
      // 验证文件分类
      if (this.selectedCategoryId == '') {
        this.$message.warning('请选择文件分类')
        return
      }
      
      // 验证文件是否存在
      if (this.fileList.length === 0) {
        this.$message.warning('请选择要上传的文件')
        return
      }
      
      // 触发上传
      this.submit()
    },

    // 添加提交上传方法
  async submit() {
    this.isUploading = true;
    try {
      // 逐个上传，避免多个大文件同时占用带宽，并准确维护整个队列状态。
      for (const file of [...this.fileList]) await this.uploadFile(file);
    } finally {
      this.isUploading = false;
    }
  },

  // 单个文件上传逻辑
  uploadFile(file) {
    const formData = new FormData();
    // 上传请求参数 - 确保类型正确
    formData.append('file', file.raw);
    formData.append('categoryId', Number(this.selectedCategoryId) || 0);
    formData.append('shareDuration', Number(this.formData.shareDuration) || 7);
    formData.append('isShared', Number(this.formData.isShared) || 0);

    // 显示COS上传提示
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    const storageType = '腾讯云COS';
    
    // 上传到COS
    return uploadFile(formData, (progressEvent) => {
        // 进度回调逻辑
        if (progressEvent.total > 0) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          file.progress = percent;
        }
      })
      .then(() => {
        // 上传成功
        const message = `${file.name} 上传成功（${storageType}，${fileSizeMB}MB）`;
        this.$message.success(message);
        // 从文件列表移除已上传的文件
        const index = this.fileList.indexOf(file);
        if (index > -1) {
          this.fileList.splice(index, 1);
        }
      })
      .catch(error => {
        console.error(`${file.name} 上传失败:`, error);
        const errorMsg = (error && error.message) || '网络错误';
        this.$message.error(`${file.name} 上传失败: ${errorMsg}`);
        file.progress = 0;
      });

  },
    
    // 清空所有文件
    clearAllFiles() {
      this.fileList = []
    },
    
    // 移除单个文件
    handleRemove(file, index) {
      this.fileList.splice(index, 1)
    },
    
    // 文件超出限制
    handleExceed() {
      this.$message.warning('最多只能上传5个文件')
    },
    
    // 检查按钮状态
    checkButtonStatus() {
      // 强制刷新计算属性
      this.$forceUpdate()
    },
    
    // 获取文件图标
    getFileIcon(name) {
      const ext = name.split('.').pop().toLowerCase()
      if (['jpg', 'png', 'jpeg', 'gif'].includes(ext)) return 'el-icon-picture'
      if (['pdf'].includes(ext)) return 'el-icon-file-pdf'
      if (['doc', 'docx'].includes(ext)) return 'el-icon-file-text'
      if (['xls', 'xlsx'].includes(ext)) return 'el-icon-table'
      if (['zip', 'rar'].includes(ext)) return 'el-icon-zip'
      return 'el-icon-file'
    },
    
    // 截断文件名
    truncateName(name) {
      return name.length > 22 ? name.substring(0, 20) + '...' : name
    },
    
    // 格式化文件大小
    formatSize(size) {
      if (size < 1024) return `${size}B`
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)}KB`
      return `${(size / (1024 * 1024)).toFixed(1)}MB`
    },
    // 删除文件
    handleDelete(index) {
      if (this.isUploading) return;
      this.fileList.splice(index, 1);
    }
  }
}
</script>

<style scoped>
.upload-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
  position: relative;
}

/* 圆形退出按钮容器 */
.logout-button-container {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 100;
}

/* 圆形退出按钮样式 */
.logout-button {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background-color: #f56c6c;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  box-shadow: 0 4px 12px rgba(245, 108, 108, 0.3);
  transition: all 0.3s ease;
}

.logout-button:hover {
  background-color: #f78989;
  box-shadow: 0 6px 16px rgba(245, 108, 108, 0.4);
}

.logout-button:active {
  transform: scale(0.95);
}

/* 表单样式 */
.file-form {
  margin-bottom: 30px;
}

/* 拖拽上传区域 */
.upload-wrapper {
  margin-bottom: 30px;
}

.upload-component {
  width: 100%;
}

.drag-area:hover {
  border-color: #409eff;
  background-color: #f5f9ff;
}

/* 操作按钮区域 - 确保可点击 */
.action-buttons {
  margin-top: 20px;
  display: flex;
  gap: 15px;
}

.start-btn, .reset-btn {
  padding: 8px 20px;
  cursor: pointer; /* 确保鼠标指针正确显示 */
}

/* 修复禁用状态样式 */
.start-btn:disabled, .reset-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 支持的格式 */
.supported-formats {
  margin: 20px 0;
  padding: 15px;
  background-color: #f5f7fa;
  border-radius: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

/* 文件列表 - 确保显示 */
.file-list-container {
  margin-top: 30px;
  padding-top: 10px;
}

.file-list-container h3 {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.file-item i {
  font-size: 20px;
  color: #409eff;
  margin-right: 15px;
}

.file-details {
  flex: 1;
}

.file-name {
  font-size: 14px;
  color: #333;
  margin-bottom: 4px;
}

.file-size {
  font-size: 12px;
  color: #999;
}

.delete-btn {
  color: #f56c6c;
}

.upload-page {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  text-align: center;
  margin: 20px 0 30px;
}

.main-container {
  max-width: 1200px; /* 适当加宽容器 */
  margin: 0 auto;
  background: #fff;
  padding: 40px; /* 增加内边距 */
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.05);
}

/* 核心：增大拖拽区域尺寸 */
.drag-area {
  width: 100%; /* 占满容器宽度 */
  min-height: 300px; /* 大幅增加高度 */
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 60px 20px; /* 增加内边距，让内容居中更美观 */
  text-align: center;
  background-color: #fafafa;
  box-sizing: border-box;
  transition: all 0.3s;
}

/* 调整内部元素比例，适应更大区域 */
.icon {
  font-size: 64px; /* 增大图标 */
  color: #409eff;
  margin-bottom: 30px; /* 增加间距 */
}

.drag-text h3 {
  font-size: 20px; /* 增大标题 */
  color: #333;
  margin-bottom: 15px;
}

.drag-text p {
  font-size: 16px; /* 增大说明文字 */
  color: #666;
  margin-bottom: 30px; /* 增加与按钮的间距 */
}

.select-file-btn {
  padding: 12px 30px; /* 增大按钮 */
  font-size: 16px;
}

/* 其他样式保持不变 */
.file-form {
  margin-bottom: 40px; /* 增加与上传区域的间距 */
}

.upload-wrapper {
  margin-bottom: 40px;
}

.action-buttons {
  margin-top: 25px;
  gap: 20px;
}

.start-btn, .reset-btn {
  padding: 10px 24px;
  font-size: 15px;
}

.supported-formats {
  margin: 30px 0;
}

.file-list-container {
  margin-top: 40px;
}

/* 响应式调整，保持比例 */
@media (max-width: 768px) {
  .drag-area {
    min-height: 250px;
    padding: 40px 15px;
  }
  
  .icon {
    font-size: 50px;
  }
  
  .drag-text h3 {
    font-size: 18px;
  }
}

/* 页面容器 */
.upload-page {
  max-width: 800px;
  margin: 20px auto;
  padding: 0 20px;
}

/* 拖拽上传区域 */
.upload-container {
  width: 100%;
  height: 220px;
  border: 2px dashed #c0c4cc;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 20px;
}

.upload-container.is-dragging {
  border-color: #409eff;
  background: linear-gradient(135deg, #ecf5ff 0%, #e0ecff 100%);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.upload-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 16px;
  transition: font-size 0.3s;
}

.upload-container:hover .upload-icon {
  font-size: 52px;
}

.upload-text {
  text-align: center;
  color: #495057;
}

.title {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
}

.subtitle {
  font-size: 12px;
  color: #868e96;
  margin-bottom: 16px;
}

.upload-btn {
  margin-top: 8px;
}

.file-input {
  display: none;
}

/* 文件列表 */
.file-list {
  border-radius: 8px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.2s;
}

.file-item:hover {
  background-color: #f8f9fa;
}

.file-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.file-icon {
  color: #409eff;
  margin-right: 12px;
  font-size: 20px;
}

.file-meta {
  display: flex;
  flex-direction: column;
}

.file-name {
  font-size: 14px;
  color: #343a40;
}

.file-size {
  font-size: 12px;
  color: #868e96;
}

.file-progress {
  flex: 1;
  margin: 0 16px;
}

.status-text {
  font-size: 12px;
  color: #67c23a;
}

.delete-btn {
  color: #f56c6c;
  transition: color 0.2s;
}

.delete-btn:hover {
  color: #ff4d4f;
}

/* 进度条样式优化 */
::v-deep .el-progress__text {
  font-size: 12px;
}

::v-deep .el-progress-bar__inner {
  background-color: #409eff;
}
</style>
